import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import type { ApiCategory, ApiCity, ApiProvider } from '../../core/api/api.types';
import { ProvidersApiService } from '../../core/api/providers-api.service';
import { ReferenceApiService } from '../../core/api/reference-api.service';
import { FilterBarComponent } from '../../shared/components/filter-bar/filter-bar.component';
import {
  FilterBarConfig,
  FilterBarValues,
} from '../../shared/components/filter-bar/filter-bar.types';

type TrustStatus = 'trusted' | 'caution' | 'pre_block' | 'blocked';

const LAST_ASSIGNED_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
};

/**
 * Renders a provider's most recent assignment date. Never-assigned providers and
 * malformed timestamps both read as an em dash — the column answers "when did
 * they last get work", and "never" is a legitimate answer, not an error.
 */
export function formatLastAssigned(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', LAST_ASSIGNED_FORMAT).format(date);
}

@Component({
  selector: 'op-providers-page',
  imports: [
    RouterModule,
    MatIconModule,
    MatProgressSpinnerModule,
    FilterBarComponent,
  ],
  templateUrl: './providers.page.html',
  styleUrl: './providers.page.scss',
})
export class ProvidersPage implements OnInit {
  private readonly providersApi = inject(ProvidersApiService);
  private readonly referenceApi = inject(ReferenceApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  // ── Reference data ────────────────────────────────────────────────────────
  readonly cities = signal<ApiCity[]>([]);
  readonly categories = signal<ApiCategory[]>([]);
  readonly refLoading = signal(true);

  readonly categoryOptions = computed(() =>
    this.categories().map(c => ({ id: c.category_id, name: c.name, vertical: c.vertical })),
  );

  readonly cityOptions = computed(() =>
    this.cities().map(c => ({ code: c.code, label: c.label })),
  );

  // ── Filter config ─────────────────────────────────────────────────────────
  readonly filterConfig: FilterBarConfig = {
    search: true,
    categories: true,
    cityCodes: true,
    trustStatus: true,
    insuranceVerified: true,
    isActive: true,
  };

  readonly filterInitialValues = signal<Partial<FilterBarValues>>({});

  // ── Providers data ────────────────────────────────────────────────────────
  readonly providers = signal<ApiProvider[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  async ngOnInit(): Promise<void> {
    try {
      const [citiesRes, categoriesRes] = await Promise.all([
        this.referenceApi.cities(),
        this.referenceApi.categories(),
      ]);
      this.cities.set(citiesRes.items);
      this.categories.set(categoriesRes.items);
    } catch (err) {
      console.warn('[Providers] Failed to load reference data', err);
    } finally {
      this.refLoading.set(false);
    }

    // Restore filters from URL
    const q = this.route.snapshot.queryParams;
    const initial: FilterBarValues = {
      search:            q['search']            || '',
      categoryIds:       q['categoryIds']  ? q['categoryIds'].split(',').filter(Boolean)  : [],
      cityCodes:         q['cityCodes']    ? q['cityCodes'].split(',').filter(Boolean)    : [],
      cityAvailability:  [],
      vertical:          '',
      trustStatus:       q['trustStatus']       || '',
      insuranceVerified: q['insuranceVerified'] || '',
      isActive:          q['isActive']          || '',
    };
    this.filterInitialValues.set(initial);
    await this.loadProviders(initial);
  }

  // ── Filter handler ────────────────────────────────────────────────────────
  async onFilterChange(values: FilterBarValues): Promise<void> {
    await this.writeUrl(values);
    await this.loadProviders(values);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  contactLabel(p: ApiProvider): string {
    const parts: string[] = [];
    if (p.contact_name?.trim()) {
      const words = p.contact_name.trim().split(/\s+/);
      const first = words[0];
      const lastInitial = words.length > 1 ? `${words[words.length - 1][0]}.` : '';
      parts.push(lastInitial ? `${first} ${lastInitial}` : first);
    }
    if (p.phone) parts.push(p.phone);
    return parts.join(' | ') || '—';
  }

  categoryNamesFor(p: ApiProvider): string {
    if (!p.category_ids?.length) return '—';
    const cats = this.categories();
    const names = p.category_ids
      .map(id => cats.find(c => c.category_id === id)?.name ?? null)
      .filter((n): n is string => n !== null);
    if (!names.length) return '—';
    if (names.length <= 2) return names.join(', ');
    return `${names[0]}, ${names[1]} +${names.length - 2}`;
  }

  cityLabelsFor(p: ApiProvider): string {
    if (!p.city_codes?.length) return 'All cities';
    const cityMap = Object.fromEntries(this.cities().map(c => [c.code, c.label]));
    return p.city_codes.map(code => cityMap[code] ?? code).join(', ');
  }

  lastAssignedLabel(p: ApiProvider): string {
    return formatLastAssigned(p.last_assigned_at);
  }

  trustLabel(status: TrustStatus): string {
    const map: Record<TrustStatus, string> = {
      trusted:   'Trusted',
      caution:   'Caution',
      pre_block: 'Pre-Block',
      blocked:   'Blocked',
    };
    return map[status] ?? status;
  }

  // ── Private ───────────────────────────────────────────────────────────────
  private async loadProviders(f: FilterBarValues): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const res = await this.providersApi.list({
        search:            f.search            || undefined,
        categoryIds:       f.categoryIds.length ? f.categoryIds : undefined,
        cityCodes:         f.cityCodes.length   ? f.cityCodes   : undefined,
        trustStatus:       f.trustStatus       || undefined,
        insuranceVerified: (f.insuranceVerified as 'true' | 'false' | '') || undefined,
        isActive:          (f.isActive         as 'true' | 'false' | '') || undefined,
      });
      this.providers.set(res.items);
    } catch (err: any) {
      this.error.set(err?.message ?? 'Failed to load providers.');
    } finally {
      this.loading.set(false);
    }
  }

  private writeUrl(f: FilterBarValues): Promise<boolean> {
    const queryParams: Record<string, string | null> = {
      search:            f.search                          || null,
      categoryIds:       f.categoryIds.length ? f.categoryIds.join(',') : null,
      cityCodes:         f.cityCodes.length   ? f.cityCodes.join(',')   : null,
      trustStatus:       f.trustStatus                    || null,
      insuranceVerified: f.insuranceVerified              || null,
      isActive:          f.isActive                       || null,
    };
    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'replace',
      replaceUrl: true,
    });
  }
}